import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert,
  InputAdornment,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Ruler, Plus, TrendingDown, TrendingUp, Minus, Edit, Save, X, Trash2 } from 'lucide-react';
import { BodyMeasurementEntry, DatabaseSchema } from '../types';
import { format } from 'date-fns';
import { WeekNavigation } from './WeekNavigation';
import { getWeekDays } from '../utils/weekUtils';

interface BodyMeasurementTrackerProps {
  database: DatabaseSchema | null;
  onAddBodyMeasurement: (measurement: BodyMeasurementEntry) => Promise<void>;
  onDeleteBodyMeasurement?: (id: string) => Promise<void>;
}

export const BodyMeasurementTracker: React.FC<BodyMeasurementTrackerProps> = ({ 
  database, 
  onAddBodyMeasurement,
  onDeleteBodyMeasurement 
}) => {
  const [measurements, setMeasurements] = useState({
    waist: '',
    chest: '',
    hips: '',
    thighs: '',
    arms: '',
    neck: '',
    shoulders: '',
  });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [editMeasurements, setEditMeasurements] = useState({
    waist: '',
    chest: '',
    hips: '',
    thighs: '',
    arms: '',
    neck: '',
    shoulders: '',
  });
  const [editNotes, setEditNotes] = useState('');
  const [quickSaveLoading, setQuickSaveLoading] = useState(false);

  const bodyMeasurements = database?.bodyMeasurements || [];
  const sortedMeasurements = [...bodyMeasurements].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const hasAnyMeasurement = Object.values(measurements).some(value => value !== '');
    if (!hasAnyMeasurement) return;

    setLoading(true);
    
    try {
      const cleanedMeasurements: any = {};
      Object.entries(measurements).forEach(([key, value]) => {
        if (value !== '') {
          cleanedMeasurements[key] = parseFloat(value);
        }
      });

      const newEntry: BodyMeasurementEntry = {
        id: `measurement_${Date.now()}`,
        date: format(new Date(), 'yyyy-MM-dd'),
        measurements: cleanedMeasurements,
        notes: notes || undefined,
      };

      await onAddBodyMeasurement(newEntry);
      
      setMeasurements({
        waist: '',
        chest: '',
        hips: '',
        thighs: '',
        arms: '',
        neck: '',
        shoulders: '',
      });
      setNotes('');
    } catch (error) {
      console.error('Error adding measurement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMeasurementChange = (field: string, value: string) => {
    setMeasurements(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getMeasurementLabel = (key: string) => {
    switch (key) {
      case 'waist': return 'Waist';
      case 'chest': return 'Chest';
      case 'hips': return 'Hips';
      case 'thighs': return 'Thighs';
      case 'arms': return 'Arms';
      case 'neck': return 'Neck';
      case 'shoulders': return 'Shoulders';
      default: return key;
    }
  };

  const getMeasurementChange = (current: BodyMeasurementEntry, previous?: BodyMeasurementEntry) => {
    if (!previous) return null;
    
    const changes: { [key: string]: number } = {};
    
    Object.keys(current.measurements).forEach(key => {
      const currentValue = current.measurements[key as keyof typeof current.measurements];
      const previousValue = previous.measurements[key as keyof typeof previous.measurements];
      
      if (currentValue !== undefined && previousValue !== undefined) {
        changes[key] = currentValue - previousValue;
      }
    });
    
    return changes;
  };

  const getTotalReduction = () => {
    if (sortedMeasurements.length < 2) return null;
    
    const latest = sortedMeasurements[0];
    const oldest = sortedMeasurements[sortedMeasurements.length - 1];
    
    let totalReduction = 0;
    let measurementCount = 0;
    
    Object.keys(latest.measurements).forEach(key => {
      const latestValue = latest.measurements[key as keyof typeof latest.measurements];
      const oldestValue = oldest.measurements[key as keyof typeof oldest.measurements];
      
      if (latestValue !== undefined && oldestValue !== undefined) {
        totalReduction += (oldestValue - latestValue);
        measurementCount++;
      }
    });
    
    return measurementCount > 0 ? totalReduction : null;
  };

  const totalReduction = getTotalReduction();

  // Get weekly measurement data
  const getWeeklyMeasurements = () => {
    const weekDays = getWeekDays(selectedWeek);
    
    return weekDays.map(day => {
      const dayEntry = bodyMeasurements.find(entry => entry.date === day.dateString);
      return {
        ...day,
        measurement: dayEntry,
        hasEntry: !!dayEntry
      };
    });
  };

  const weeklyMeasurements = getWeeklyMeasurements();

  // Handle editing a specific day
  const handleEditDay = (dateString: string) => {
    const dayData = weeklyMeasurements.find(d => d.dateString === dateString);
    if (dayData?.measurement) {
      const measurements: any = {
        waist: '',
        chest: '',
        hips: '',
        thighs: '',
        arms: '',
        neck: '',
        shoulders: '',
      };
      
      Object.entries(dayData.measurement.measurements).forEach(([key, value]) => {
        measurements[key] = value?.toString() || '';
      });
      
      setEditMeasurements(measurements);
      setEditNotes(dayData.measurement.notes || '');
    } else {
      setEditMeasurements({
        waist: '',
        chest: '',
        hips: '',
        thighs: '',
        arms: '',
        neck: '',
        shoulders: '',
      });
      setEditNotes('');
    }
    setEditingDay(dateString);
  };

  const handleDeleteMeasurement = async (dateString: string) => {
    if (!onDeleteBodyMeasurement) return;
    
    const dayData = weeklyMeasurements.find(d => d.dateString === dateString);
    if (!dayData?.hasEntry || !dayData.measurement) return;
    
    const confirmDelete = window.confirm(`Delete measurements from ${format(new Date(dateString), 'MMM dd, yyyy')}?`);
    if (confirmDelete) {
      await onDeleteBodyMeasurement(dayData.measurement.id);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingDay) return;
    
    const hasAnyMeasurement = Object.values(editMeasurements).some(value => value !== '');
    if (!hasAnyMeasurement) return;
    
    setQuickSaveLoading(true);
    try {
      const dateObj = new Date(editingDay);
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date:', editingDay);
        return;
      }

      const cleanedMeasurements: any = {};
      Object.entries(editMeasurements).forEach(([key, value]) => {
        if (value !== '') {
          cleanedMeasurements[key] = parseFloat(value);
        }
      });

      const existingEntry = bodyMeasurements.find(e => e.date === editingDay);
      const newEntry: BodyMeasurementEntry = {
        id: existingEntry?.id || `measurement_${Date.now()}`,
        date: editingDay,
        measurements: cleanedMeasurements,
        notes: editNotes || undefined,
      };

      await onAddBodyMeasurement(newEntry);
      setEditingDay(null);
    } catch (error) {
      console.error('Error saving measurement entry:', error);
    } finally {
      setQuickSaveLoading(false);
    }
  };

  const handleEditMeasurementChange = (field: string, value: string) => {
    setEditMeasurements(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => {
    return (
      <div hidden={value !== index}>
        {value === index && <Box>{children}</Box>}
      </div>
    );
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Body Measurements
      </Typography>

      <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Daily Entry" />
        <Tab label="Weekly View" />
      </Tabs>

      <TabPanel value={currentTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Plus size={24} color="#6366f1" />
                <Typography variant="h6">Record Measurements</Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {Object.entries(measurements).map(([key, value]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <TextField
                        fullWidth
                        label={getMeasurementLabel(key)}
                        type="number"
                        value={value}
                        onChange={(e) => handleMeasurementChange(key, e.target.value)}
                        inputProps={{ step: "0.1", min: "0" }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
                
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{ mt: 2, mb: 3 }}
                  placeholder="Any observations or notes about your measurements..."
                />
                
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={!Object.values(measurements).some(v => v !== '') || loading}
                  sx={{ py: 1.5 }}
                >
                  {loading ? 'Recording...' : 'Record Measurements'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {sortedMeasurements.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Progress Overview
                </Typography>
                
                {totalReduction !== null && (
                  <Alert 
                    severity={totalReduction > 0 ? 'success' : totalReduction < 0 ? 'warning' : 'info'}
                    sx={{ mb: 2 }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {totalReduction > 0 ? <TrendingDown size={16} /> : 
                       totalReduction < 0 ? <TrendingUp size={16} /> : <Minus size={16} />}
                      <Typography variant="body2">
                        {totalReduction > 0 ? 
                          `Total reduction: ${totalReduction.toFixed(1)} cm across all measurements` :
                          totalReduction < 0 ?
                          `Total increase: ${Math.abs(totalReduction).toFixed(1)} cm across all measurements` :
                          'No change in measurements'
                        }
                      </Typography>
                    </Box>
                  </Alert>
                )}

                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip 
                    icon={<Ruler />} 
                    label={`${sortedMeasurements.length} recordings`}
                    variant="outlined"
                  />
                  {sortedMeasurements[0] && (
                    <Chip 
                      label={`Latest: ${format(new Date(sortedMeasurements[0].date), 'MMM dd')}`}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Measurement History
              </Typography>
              
              {sortedMeasurements.length === 0 ? (
                <Typography color="text.secondary">
                  No measurements recorded yet. Start tracking your body measurements to monitor your progress!
                </Typography>
              ) : (
                <List>
                  {sortedMeasurements.map((entry, index) => {
                    const previous = sortedMeasurements[index + 1];
                    const changes = getMeasurementChange(entry, previous);
                    
                    return (
                      <React.Fragment key={entry.id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography fontWeight="bold">
                                {format(new Date(entry.date), 'MMM dd, yyyy')}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Grid container spacing={1} sx={{ mt: 1 }}>
                                  {Object.entries(entry.measurements).map(([key, value]) => {
                                    const change = changes?.[key];
                                    return (
                                      <Grid item xs={6} key={key}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                          <Typography variant="body2" color="text.secondary">
                                            {getMeasurementLabel(key)}:
                                          </Typography>
                                          <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" fontWeight="bold">
                                              {value} cm
                                            </Typography>
                                            {change !== undefined && change !== 0 && (
                                              <Chip 
                                                size="small"
                                                label={`${change > 0 ? '+' : ''}${change.toFixed(1)}`}
                                                color={change < 0 ? 'success' : 'error'}
                                                sx={{ height: 16, fontSize: '0.6rem' }}
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                                {entry.notes && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                    "{entry.notes}"
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < sortedMeasurements.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <Box mb={3}>
          <WeekNavigation
            currentWeek={selectedWeek}
            onWeekChange={setSelectedWeek}
          />
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Weekly Measurements Overview
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>Waist</TableCell>
                    <TableCell>Chest</TableCell>
                    <TableCell>Hips</TableCell>
                    <TableCell>Arms</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklyMeasurements.map((day) => {
                    const measurement = day.measurement;
                    const measurementCount = measurement ? Object.keys(measurement.measurements).length : 0;
                    
                    return (
                      <TableRow 
                        key={day.dateString}
                        sx={{
                          backgroundColor: day.isToday ? 'rgba(99, 102, 241, 0.08)' : 'inherit'
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={day.isToday ? 'bold' : 'normal'}>
                              {day.dayShort}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(day.date, 'MM/dd')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {measurement?.measurements.waist ? `${measurement.measurements.waist} cm` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {measurement?.measurements.chest ? `${measurement.measurements.chest} cm` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {measurement?.measurements.hips ? `${measurement.measurements.hips} cm` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {measurement?.measurements.arms ? `${measurement.measurements.arms} cm` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {measurement ? (
                            <Chip
                              size="small"
                              label={`${measurementCount} measurements`}
                              color="success"
                              variant="filled"
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="No data"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" justifyContent="center" gap={0.5}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditDay(day.dateString)}
                              color="primary"
                            >
                              <Edit size={16} />
                            </IconButton>
                            {day.hasEntry && onDeleteBodyMeasurement && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteMeasurement(day.dateString)}
                                color="error"
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Edit Dialog */}
      <Dialog open={!!editingDay} onClose={() => setEditingDay(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDay && (
            `${weeklyMeasurements.find(d => d.dateString === editingDay)?.hasEntry ? 'Edit' : 'Add'} Measurements - ${format(new Date(editingDay), 'EEEE, MMM dd')}`
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.entries(editMeasurements).map(([key, value]) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  label={getMeasurementLabel(key)}
                  type="number"
                  value={value}
                  onChange={(e) => handleEditMeasurementChange(key, e.target.value)}
                  inputProps={{ step: "0.1", min: "0" }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                  }}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Any observations or notes about your measurements..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingDay(null)}>
            <X size={16} style={{ marginRight: 4 }} />
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={quickSaveLoading || !Object.values(editMeasurements).some(v => v !== '')}
          >
            <Save size={16} style={{ marginRight: 4 }} />
            {quickSaveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};